<script setup>
import {ref, computed, onMounted, onUnmounted, watch} from 'vue'
import {useStore} from 'vuex'
import { FeatureCollectionRenderer } from '@/components/map'
import { HOST, API } from '@/utils/http'
import { useToast } from '@/components/ui/toast'

const { toast } = useToast()
const store = useStore()
const map = computed(() => props.map)
const selectedRange = computed(() => store.state.layers.selectedRange)
const selectedFeature = computed(() => store.state.layers.selectedFeature)
const projectFilters = computed(() => store.state.geo.projectFilters)
const featureCollectionRendererRef = ref(null)

const emit = defineEmits(['isLoaded', 'datesLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  layer: {
    type: Object,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})

const isLoadingDates = ref(false)
const layerDates = ref([])

const storeLayer = computed(() => {
  const orgId = props.orgId
  return store.getters['layers/getOrgLayerById'](orgId, props.code)
})

const features = computed(() => {
  if (storeLayer.value?.properties?.features) {
    return storeLayer.value.properties.features;
  }
  return [];
})

const validFeatures = computed(() => {
  return features.value.filter(f => f.geometry && f.geometry.type)
})

const featureCollection = computed(() => {
  if (storeLayer.value?.properties?.featureCollection) {
    const fc = storeLayer.value.properties.featureCollection;
    return {
      ...fc,
      features: (fc.features || []).filter(f =>
        (f.geometry && f.geometry.type) ||
        (f.properties?.is_heavy && f.properties?.center_lat != null && f.properties?.center_lng != null)
      )
    };
  }
  return {
    type: 'FeatureCollection',
    features: validFeatures.value
  };
})

const isLoadingFeatures = ref(false)
const featuresCount = computed(() => featureCollection.value.features.length)

const processDateFormat = (dateItem) => {
  if (!dateItem) return null;

  // Ensure the date property is present
  if (!dateItem.date && !dateItem.hour) return null;

  const dateStr = dateItem.date || dateItem.hour;

  // Create a standardized date object
  return {
    date: dateStr,
    count: dateItem.count || 1,
    label: dateItem.label || `${dateItem.count || 1} events`,
    vessels: dateItem.vessels || [],
    features: dateItem.features || []
  };
};

const fetchDates = async () => {
  try {
    isLoadingDates.value = true

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${props.orgId}/features/dates`,
      params: { layer_code: props.code }
    })

    if (response.data && response.data.layers && Array.isArray(response.data.layers)) {
      const processedLayers = response.data.layers.map(layer => {
        const processedDates = Array.isArray(layer.dates)
          ? layer.dates.map(processDateFormat)
          : [];

        return {
          id: layer.id || 'unknown',
          name: layer.name || 'Unknown Template',
          color: layer.color || 'blue',
          dates: processedDates
        };
      });

      layerDates.value = processedLayers;
      emit('datesLoaded', processedLayers);

      if (processedLayers.length > 0) {
        try {
          store.commit('layers/ADD_DATES', processedLayers);
        } catch (storeError) {
          console.error('Error updating store with dates:', storeError);
        }
      }
    }
  } catch (error) {
    console.error('Error fetching dates:', error)
    toast({
      title: 'Error loading dates',
      description: 'Failed to fetch dates for the layer',
      variant: 'destructive'
    })
  } finally {
    isLoadingDates.value = false
  }
}

const fetchFeatures = async () => {
  isLoadingFeatures.value = true;

  try {
    const [startDate, endDate] = selectedRange.value || [];

    const params = { layer_code: props.code };
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }

    const filters = projectFilters.value
    if (filters) {
      if (filters.templateIds?.length > 0) {
        params.template_ids = filters.templateIds.join(',')
      }
      if (filters.memberIds?.length > 0) {
        params.member_ids = filters.memberIds.join(',')
      }
      if (filters.searchText?.trim()) {
        params.search_text = filters.searchText
      }
      if (filters.isPublicData) {
        params.is_public = true
      }
      if (filters.geometryTypes?.length > 0) {
        params.geometry_types = filters.geometryTypes.join(',')
      }
    }

    const response = await API({
      method: 'GET',
      url: `${HOST}/organization/${props.orgId}/features`,
      params
    });

    if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      const orgId = props.orgId;
      const layerId = props.code;

      await store.dispatch('layers/updateOrgLayerFeatures', {
        orgId,
        layerId,
        features: response.data.features
      });

      emit('isLoaded', true);
    }
  } catch (error) {
    console.error('Error loading features:', error);
    toast({
      title: 'Error loading features',
      description: 'Failed to fetch features for the layer',
      variant: 'destructive'
    });
  } finally {
    isLoadingFeatures.value = false;
  }
}

let fetchFeaturesTimeout = null
const debouncedFetchFeatures = () => {
  if (fetchFeaturesTimeout) {
    clearTimeout(fetchFeaturesTimeout)
  }
  fetchFeaturesTimeout = setTimeout(() => {
    fetchFeatures()
  }, 500)
}

let needsDates = true

watch(
  [computed(() => props.isVisible), selectedRange],
  async ([visible]) => {
    if (!visible) {
      if (layerDates.value.length > 0) {
        store.commit('layers/REMOVE_DATES', layerDates.value.map(d => d.id))
      }
      return
    }

    if (needsDates) {
      needsDates = false
      await fetchDates()
    } else if (layerDates.value.length > 0) {
      store.commit('layers/ADD_DATES', layerDates.value)
    }

    debouncedFetchFeatures()
  },
  { immediate: true }
)

// Watch project filters to update map features
watch(projectFilters, () => {
  if (props.isVisible) {
    debouncedFetchFeatures()
    fetchDates()
  }
}, { deep: true })

// Watch for organization changes to refetch dates
watch(() => props.orgId, async (newOrgId, oldOrgId) => {
  if (newOrgId && newOrgId !== oldOrgId) {
    store.commit('layers/CLEAR_DATES');
    needsDates = true
    if (props.isVisible) {
      needsDates = false
      await fetchDates();
      await fetchFeatures();
    }
  }
});

watch(selectedFeature, (newSelectedFeature) => {
  if (newSelectedFeature?.feature && newSelectedFeature.isVisible &&
      newSelectedFeature.feature.properties?.code &&
      validFeatures.value.some(f => f.properties?.code === newSelectedFeature.feature.properties.code)) {
    if (featureCollectionRendererRef.value) {
      featureCollectionRendererRef.value.openPopup(newSelectedFeature.feature)
    }
  }
}, { deep: true })

onUnmounted(() => {
  if (layerDates.value.length > 0) {
    store.commit('layers/REMOVE_DATES', layerDates.value.map(d => d.id))
  }
})
</script>

<template>
  <FeatureCollectionRenderer
    ref="featureCollectionRendererRef"
    v-if="featuresCount > 0 && map"
    :map="map"
    :features="featureCollection"
    :group-id="groupId"
    :before-id="beforeId"
    :is-visible="isVisible"
    :enable-clustering="true"
    :enable-image-markers="true"
    :enable-age-based-styling="false"
  />
</template>
